import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Users, TrendingUp, DollarSign } from 'lucide-react';
interface AffiliateSignupPageProps {
  onNavigate?: (path: string) => void;
}
export function AffiliateSignupPage({
  onNavigate
}: AffiliateSignupPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const response = await fetch('https://formspree.io/f/xeeqdwke', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setIsSubmitted(true);
        form.reset();
      }
    } catch (error) {
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-176">
      <Navbar onNavigate={onNavigate} data-id="element-177" />

      <section className="py-24" data-id="element-178">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-179">
          {/* Header */}
          <div className="text-center mb-16" data-id="element-180">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1f2933] mb-4" data-id="element-181">
              Become an Affiliate Partner
            </h1>
            <p className="text-lg font-mono text-[#9ca3af] mb-8" data-id="element-182">
              Help professionals build authority. Earn while you impact.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-183">
            <div className="border-2 border-dashed border-salon-red p-6 text-center" data-id="element-184">
              <DollarSign className="w-10 h-10 text-salon-red mx-auto mb-3" data-id="element-185" />
              <h3 className="font-bold mb-2" data-id="element-186">Earn Commission</h3>
              <p className="text-sm text-[#9ca3af] font-mono" data-id="element-187">
                Competitive rates on every referral
              </p>
            </div>
            <div className="border-2 border-dashed border-salon-teal p-6 text-center" data-id="element-188">
              <Users className="w-10 h-10 text-salon-teal mx-auto mb-3" data-id="element-189" />
              <h3 className="font-bold mb-2" data-id="element-190">Support Your Community</h3>
              <p className="text-sm text-[#9ca3af] font-mono" data-id="element-191">
                Provide real value to your audience
              </p>
            </div>
            <div className="border-2 border-dashed border-salon-yellow p-6 text-center" data-id="element-192">
              <TrendingUp className="w-10 h-10 text-salon-yellow mx-auto mb-3" data-id="element-193" />
              <h3 className="font-bold mb-2" data-id="element-194">Grow Together</h3>
              <p className="text-sm text-[#9ca3af] font-mono" data-id="element-195">
                Build authority alongside your referrals
              </p>
            </div>
          </div>

          {/* Form */}
          {isSubmitted ? <div className="bg-white border-2 border-salon-teal p-12 text-center" data-id="element-196">
              <h2 className="text-2xl font-bold text-salon-teal mb-4" data-id="element-197">
                Application Submitted!
              </h2>
              <p className="text-[#9ca3af] font-mono mb-6" data-id="element-198">
                Thank you for your interest in becoming an affiliate partner.
                We'll review your application and get back to you within 48
                hours.
              </p>
              <Button variant="secondary" onClick={() => onNavigate && onNavigate('/')} data-id="element-199">
                RETURN_HOME
              </Button>
            </div> : <div className="bg-white border-2 border-dashed border-[#9ca3af] p-8 md:p-12" data-id="element-200">
              <h2 className="text-2xl font-bold mb-6" data-id="element-201">Affiliate Application</h2>
              <p className="text-sm text-[#9ca3af] font-mono mb-8" data-id="element-202">
                Tell us about yourself and your community. We're looking for
                partners who align with our mission.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-203">
                {/* Full Name */}
                <div data-id="element-204">
                  <label htmlFor="fullName" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-205">
                    FULL_NAME *
                  </label>
                  <input type="text" id="fullName" name="fullName" required className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" placeholder="John Doe" data-id="element-206" />
                </div>

                {/* Email */}
                <div data-id="element-207">
                  <label htmlFor="email" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-208">
                    EMAIL_ADDRESS *
                  </label>
                  <input type="email" id="email" name="email" required className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" placeholder="john@example.com" data-id="element-209" />
                </div>

                {/* Phone */}
                <div data-id="element-210">
                  <label htmlFor="phone" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-211">
                    PHONE_NUMBER *
                  </label>
                  <input type="tel" id="phone" name="phone" required className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" placeholder="+234 xxx xxx xxxx" data-id="element-212" />
                </div>

                {/* Platform/Niche */}
                <div data-id="element-213">
                  <label htmlFor="platform" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-214">
                    PRIMARY_PLATFORM_&_NICHE *
                  </label>
                  <input type="text" id="platform" name="platform" required className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" placeholder="e.g., LinkedIn - Business Coaching" data-id="element-215" />
                </div>

                {/* Audience Size */}
                <div data-id="element-216">
                  <label htmlFor="audienceSize" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-217">
                    COMMUNITY/AUDIENCE_SIZE *
                  </label>
                  <select id="audienceSize" name="audienceSize" required className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none bg-white" data-id="element-218">
                    <option value="" data-id="element-219">Select range</option>
                    <option value="0-500" data-id="element-220">0 - 500</option>
                    <option value="500-1000" data-id="element-221">500 - 1,000</option>
                    <option value="1000-5000" data-id="element-222">1,000 - 5,000</option>
                    <option value="5000-10000" data-id="element-223">5,000 - 10,000</option>
                    <option value="10000+" data-id="element-224">10,000+</option>
                  </select>
                </div>

                {/* Expected Referrals */}
                <div data-id="element-225">
                  <label htmlFor="expectedReferrals" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-226">
                    ESTIMATED_MONTHLY_REFERRALS *
                  </label>
                  <select id="expectedReferrals" name="expectedReferrals" required className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none bg-white" data-id="element-227">
                    <option value="" data-id="element-228">Select range</option>
                    <option value="1-5" data-id="element-229">1 - 5 signups</option>
                    <option value="5-10" data-id="element-230">5 - 10 signups</option>
                    <option value="10-20" data-id="element-231">10 - 20 signups</option>
                    <option value="20+" data-id="element-232">20+ signups</option>
                  </select>
                </div>

                {/* Why Partner */}
                <div data-id="element-233">
                  <label htmlFor="whyPartner" className="block font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-234">
                    WHY_DO_YOU_WANT_TO_PARTNER? *
                  </label>
                  <textarea id="whyPartner" name="whyPartner" required rows={4} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none resize-none" placeholder="Tell us about your motivation and how this aligns with your community..." data-id="element-235" />
                </div>

                {/* Submit Button */}
                <div className="pt-4" data-id="element-236">
                  <Button type="submit" size="large" disabled={isSubmitting} className="w-full bg-salon-red hover:bg-salon-red/90 border-salon-red" data-id="element-237">
                    {isSubmitting ? 'SUBMITTING...' : 'SUBMIT_APPLICATION'}
                  </Button>
                </div>

                <p className="text-xs text-[#9ca3af] font-mono text-center" data-id="element-238">
                  We review all applications within 48 hours
                </p>
              </form>
            </div>}
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-239" />
    </div>;
}